<template>
  <div class="mask" @click.self="onClickOutSide">
    <div class="filter-dialog">
      <!-- ヘッダー -->
      <div class="filter-header">
        <span class="filter-header-title">{{ $t("homePage.novelList.filter.dialogTitle") }}</span>
        <button class="filter-close-button" @click="onClickOutSide">
          <span>&times;</span>
        </button>
      </div>

      <!-- コンテンツ -->
      <div class="filter-body">
        <!-- ステータス -->
        <div class="filter-section">
          <h4 class="filter-section-title">{{ $t("homePage.novelList.filter.workStatus") }}</h4>
          <div class="filter-chips">
            <button
              v-for="option in workStatusOptions"
              :key="option.id"
              class="filter-chip"
              :class="{ active: selectedWorkStatusIds.includes(option.id) }"
              @click="toggleWorkStatus(option.id)"
            >
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- ジャンル -->
        <div class="filter-section">
          <h4 class="filter-section-title">{{ $t("homePage.novelList.filter.genre") }}</h4>
          <div class="filter-chips">
            <button
              v-for="option in genreOptions"
              :key="option.id"
              class="filter-chip"
              :class="{ active: selectedGenreIds.includes(option.id) }"
              @click="toggleGenre(option.id)"
            >
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- カテゴリ -->
        <div class="filter-section">
          <h4 class="filter-section-title">{{ $t("homePage.novelList.filter.category") }}</h4>
          <div class="filter-chips">
            <button
              v-for="option in categoryOptions"
              :key="option.id"
              class="filter-chip"
              :class="{ active: selectedCategoryIds.includes(option.id) }"
              @click="toggleCategory(option.id)"
            >
              {{ option.name }}
            </button>
          </div>
        </div>

        <!-- グループ -->
        <div v-if="groupOptions.length > 0" class="filter-section">
          <h4 class="filter-section-title">{{ $t("homePage.novelList.filter.group") }}</h4>
          <div class="filter-chips">
            <button
              v-for="option in groupOptions"
              :key="option.groupId"
              class="filter-chip"
              :class="{ active: selectedGroupIds.includes(option.groupId) }"
              @click="toggleGroup(option.groupId)"
            >
              {{ option.name }}
            </button>
          </div>
        </div>
      </div>

      <!-- アクション -->
      <div class="filter-actions">
        <button class="filter-apply-button" @click="onApply">
          {{ $t("homePage.novelList.filter.apply") }}
        </button>
        <button class="filter-cancel-button" @click="onCancel">
          {{ $t("common.cancel") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { NovelSetting } from "@/lib/models";
import { NovelGroup, NovelGroupSummary } from "@/lib/models/novelGroup";
import {
  getTranslatedWorkStatusList,
  getTranslatedGenreList,
  getTranslatedCategoryList,
} from "@/lib/utils/novelSettingsHelper";

export interface NovelFilterDialogProps {
  workStatuses: NovelSetting[];
  genres: NovelSetting[];
  categories: NovelSetting[];
  groups: NovelGroupSummary[];
}

export interface NovelFilterDialogResult {
  workStatuses: NovelSetting[];
  genres: NovelSetting[];
  categories: NovelSetting[];
  groups: NovelGroupSummary[];
}

function toggleSelection(list: string[], id: string) {
  const index = list.indexOf(id);
  if (index >= 0) {
    list.splice(index, 1);
  } else {
    list.push(id);
  }
}

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    workStatuses: {
      type: Array,
      default: () => [],
    },
    genres: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedWorkStatusIds: (this.workStatuses as NovelSetting[]).map((ws) => ws.id),
      selectedGenreIds: (this.genres as NovelSetting[]).map((g) => g.id),
      selectedCategoryIds: (this.categories as NovelSetting[]).map((c) => c.id),
      selectedGroupIds: (this.groups as NovelGroupSummary[]).map((g) => g.groupId),
    };
  },

  computed: {
    workStatusOptions() {
      const t = (key: string) => this.$t(key) as string;
      return getTranslatedWorkStatusList(t);
    },
    genreOptions() {
      const t = (key: string) => this.$t(key) as string;
      return getTranslatedGenreList(t);
    },
    categoryOptions() {
      const t = (key: string) => this.$t(key) as string;
      return getTranslatedCategoryList(t);
    },
    groupOptions(): NovelGroupSummary[] {
      return (this.$store.getters["novelGroupModule/groups"] as NovelGroup[]).map((g) => ({
        groupId: g.groupId,
        name: g.name,
      }));
    },
  },

  methods: {
    toggleWorkStatus(id: string) {
      toggleSelection(this.selectedWorkStatusIds, id);
    },
    toggleGenre(id: string) {
      toggleSelection(this.selectedGenreIds, id);
    },
    toggleCategory(id: string) {
      toggleSelection(this.selectedCategoryIds, id);
    },
    toggleGroup(groupId: string) {
      toggleSelection(this.selectedGroupIds, groupId);
    },
    onApply() {
      const result: NovelFilterDialogResult = {
        workStatuses: this.workStatusOptions.filter((ws) => this.selectedWorkStatusIds.includes(ws.id)),
        genres: this.genreOptions.filter((g) => this.selectedGenreIds.includes(g.id)),
        categories: this.categoryOptions.filter((c) => this.selectedCategoryIds.includes(c.id)),
        groups: this.groupOptions.filter((g) => this.selectedGroupIds.includes(g.groupId)),
      };
      this.$close(result);
    },
    onCancel() {
      this.$close(null);
    },
    onClickOutSide() {
      this.$close(null);
    },
  },
});

interface Props {
  workStatuses: NovelSetting[];
  genres: NovelSetting[];
  categories: NovelSetting[];
  groups: NovelGroupSummary[];
}

interface Data {
  selectedWorkStatusIds: string[];
  selectedGenreIds: string[];
  selectedCategoryIds: string[];
  selectedGroupIds: string[];
}

interface Computed {
  workStatusOptions: NovelSetting[];
  genreOptions: NovelSetting[];
  categoryOptions: NovelSetting[];
  groupOptions: NovelGroupSummary[];
}

interface Methods {
  toggleWorkStatus(id: string): void;
  toggleGenre(id: string): void;
  toggleCategory(id: string): void;
  toggleGroup(groupId: string): void;
  onApply(): void;
  onCancel(): void;
  onClickOutSide(): void;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 9100;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.filter-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

/* ヘッダー */
.filter-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.filter-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.filter-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  span {
    font-size: var(--font-size-xl);
    line-height: 1;
    color: var(--color-text-normal);
  }
}

/* コンテンツ */
.filter-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 0;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  margin: 0 0 10px;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-chip {
  padding: 7px 16px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  border: none;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    background-color: var(--color-background-invert);
    color: var(--color-text-invert);
  }
}

/* アクション */
.filter-actions {
  padding: 12px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-apply-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}

.filter-cancel-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
